/**
 * Relationship Types — minimal tenant configuration editor.
 * Phase 1.5 Milestone F1 (corrections §3).
 *
 * RBAC:
 *   - owner_admin: full CRUD (create / edit label+desc / activate /
 *                  deactivate / reorder / archive).
 *   - manager_trainer: read-only.
 *   - representative / viewer: no administration controls; read still allowed.
 *
 * Contract:
 *   - `code` is immutable after creation.
 *   - Archive is soft-delete; no hard-delete.
 *   - Referenced archived types stay visible when `Show archived` is on.
 *   - Reordering rejects unknown/duplicate/cross-tenant IDs server-side.
 *   - Every mutation refetches server truth (optimistic-concurrency safe).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, toastError } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Settings2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useEffectiveRole } from "../../lib/roles";

type Rt = {
  id: string;
  code: string;
  label: string;
  description: string | null;
  order: number;
  active: boolean;
  archived: boolean;
  system_default: boolean;
  version: number;
  referenced_at_archive?: boolean;
};

export default function RelationshipTypes() {
  const role = useEffectiveRole();
  const canWrite = role === "owner_admin";

  const [rows, setRows] = useState<Rt[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get<Rt[]>(
        `/tenant/relationship-types?include_archived=${showArchived}`,
      );
      setRows(data);
    } catch (e: unknown) {
      setError("Could not load relationship types.");
      setRows([]);
      toastError(e);
    }
  }, [showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHeader
        title="Relationship Types"
        subtitle="Tenant-owned configuration for Contact-Organization relationships."
      />
      <div
        className="max-w-5xl mx-auto p-6 md:p-8 grid gap-6"
        data-testid="relationship-types-page"
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived-toggle"
            />
            Show archived
          </label>
          {canWrite ? (
            <CreateForm onCreated={load} />
          ) : (
            <div className="text-sm text-slate-500">
              Read-only for {role.replace("_", " ")}.
            </div>
          )}
        </div>

        {error && (
          <div
            role="alert"
            data-testid="rt-error"
            className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        {rows === null && (
          <div
            className="text-center py-16 px-4"
            data-testid="rt-loading"
            role="status"
          >
            <Loader2 className="w-8 h-8 mx-auto text-slate-400 mb-3 animate-spin" />
            <h3 className="text-sm font-medium text-slate-800">Loading…</h3>
          </div>
        )}

        {rows && rows.length === 0 && (
          <div
            className="text-center py-16 px-4"
            data-testid="rt-empty"
          >
            <Settings2 className="w-8 h-8 mx-auto text-slate-400 mb-3" />
            <h3 className="text-sm font-medium text-slate-800">
              No relationship types yet
            </h3>
          </div>
        )}

        {rows && rows.length > 0 && (
          <TypeList rows={rows} canWrite={canWrite} onChanged={load} />
        )}
      </div>
    </>
  );
}

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim() || !label.trim()) return;
    setBusy(true);
    try {
      await api.post("/tenant/relationship-types", {
        code: code.trim(),
        label: label.trim(),
        description: description.trim() || null,
      });
      toast.success("Relationship type created");
      setOpen(false);
      setCode("");
      setLabel("");
      setDescription("");
      onCreated();
    } catch (e: unknown) {
      toastError(e, "Could not create relationship type.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        data-testid="rt-create-open-btn"
        onClick={() => setOpen(true)}
      >
        New relationship type
      </Button>
    );
  }
  return (
    <div
      className="w-full grid gap-3 rounded-md border border-slate-200 bg-white p-4"
      data-testid="rt-create-form"
    >
      <div className="grid gap-1">
        <Label htmlFor="rt-code">
          Code <span className="text-slate-400 text-xs">(immutable)</span>
        </Label>
        <Input
          id="rt-code"
          data-testid="rt-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. investor"
          disabled={busy}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="rt-label">Label</Label>
        <Input
          id="rt-label"
          data-testid="rt-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={busy}
        />
      </div>
      <div className="grid gap-1">
        <Label htmlFor="rt-desc">Description</Label>
        <Textarea
          id="rt-desc"
          data-testid="rt-desc-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={busy}
          rows={2}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          data-testid="rt-create-cancel"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          data-testid="rt-create-submit"
          onClick={submit}
          disabled={busy || !code.trim() || !label.trim()}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
        </Button>
      </div>
    </div>
  );
}

function TypeList({
  rows,
  canWrite,
  onChanged,
}: {
  rows: Rt[];
  canWrite: boolean;
  onChanged: () => void;
}) {
  // Local order state so reorder is a single "Apply order" click.
  const [localIds, setLocalIds] = useState<string[]>(() =>
    rows.map((r) => r.id),
  );
  useEffect(() => {
    setLocalIds(rows.map((r) => r.id));
  }, [rows]);

  const byId = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, r])),
    [rows],
  );
  const ordered = useMemo(
    () => localIds.map((id) => byId[id]).filter(Boolean) as Rt[],
    [localIds, byId],
  );

  const dirty = useMemo(
    () => JSON.stringify(localIds) !== JSON.stringify(rows.map((r) => r.id)),
    [localIds, rows],
  );

  const move = (id: string, dir: -1 | 1) => {
    setLocalIds((cur) => {
      const idx = cur.indexOf(id);
      if (idx < 0) return cur;
      const next = [...cur];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return cur;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const applyOrder = async () => {
    try {
      await api.post("/tenant/relationship-types/reorder", {
        ordered_ids: localIds,
      });
      toast.success("Order updated");
      onChanged();
    } catch (e: unknown) {
      toastError(e, "Could not apply order.");
    }
  };

  return (
    <div className="grid gap-3">
      {canWrite && dirty && (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            data-testid="rt-order-reset"
            onClick={() => setLocalIds(rows.map((r) => r.id))}
          >
            Reset order
          </Button>
          <Button data-testid="rt-order-apply" onClick={applyOrder}>
            Apply order
          </Button>
        </div>
      )}
      <ul className="grid gap-2">
        {ordered.map((r, i) => (
          <li
            key={r.id}
            data-testid={`rt-row-${r.code}`}
            className={`grid grid-cols-[1fr_auto] items-start gap-4 rounded-md border p-3 ${
              r.archived
                ? "border-slate-200 bg-slate-50 opacity-70"
                : r.active
                ? "border-slate-200 bg-white"
                : "border-slate-200 bg-slate-50"
            }`}
          >
            <RowEditor row={r} canWrite={canWrite} onChanged={onChanged} />
            {canWrite && !r.archived && (
              <div className="flex flex-col gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`rt-move-up-${r.code}`}
                  onClick={() => move(r.id, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`rt-move-down-${r.code}`}
                  onClick={() => move(r.id, 1)}
                  disabled={i === ordered.length - 1}
                  aria-label="Move down"
                >
                  ↓
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RowEditor({
  row,
  canWrite,
  onChanged,
}: {
  row: Rt;
  canWrite: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(row.label);
  const [description, setDescription] = useState(row.description ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLabel(row.label);
    setDescription(row.description ?? "");
  }, [row.label, row.description]);

  const patch = async (payload: Record<string, unknown>) => {
    setBusy(true);
    try {
      await api.patch(`/tenant/relationship-types/${row.id}`, {
        version: row.version,
        ...payload,
      });
      onChanged();
    } catch (e: unknown) {
      toastError(e, "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    setBusy(true);
    try {
      await api.post(`/tenant/relationship-types/${row.id}/archive`, {
        version: row.version,
      });
      toast.success("Archived");
      onChanged();
    } catch (e: unknown) {
      toastError(e, "Archive failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <code className="font-mono text-xs text-slate-500">{row.code}</code>
        {row.archived && (
          <span
            data-testid={`rt-archived-badge-${row.code}`}
            className="rounded bg-slate-200 px-2 py-0.5 text-[11px] uppercase tracking-wide text-slate-600"
          >
            archived
          </span>
        )}
        {!row.active && !row.archived && (
          <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-amber-700">
            inactive
          </span>
        )}
        {row.system_default && (
          <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-slate-500">
            template default
          </span>
        )}
        {row.referenced_at_archive && (
          <span
            data-testid={`rt-referenced-flag-${row.code}`}
            className="rounded bg-blue-100 px-2 py-0.5 text-[11px] uppercase tracking-wide text-blue-700"
          >
            in use at archive
          </span>
        )}
      </div>
      {editing && canWrite ? (
        <div className="grid gap-2">
          <Input
            data-testid={`rt-edit-label-${row.code}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy}
          />
          <Textarea
            data-testid={`rt-edit-desc-${row.code}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            disabled={busy}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              disabled={busy}
              data-testid={`rt-cancel-${row.code}`}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                await patch({
                  label: label.trim(),
                  description: description.trim() || null,
                });
                setEditing(false);
              }}
              disabled={busy || !label.trim()}
              data-testid={`rt-save-${row.code}`}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-sm text-slate-800">{row.label}</div>
          {row.description && (
            <div className="text-xs text-slate-500">{row.description}</div>
          )}
          {canWrite && !row.archived && (
            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                data-testid={`rt-edit-${row.code}`}
              >
                Edit
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => patch({ active: !row.active })}
                data-testid={`rt-toggle-active-${row.code}`}
                disabled={busy}
              >
                {row.active ? "Deactivate" : "Activate"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={archive}
                data-testid={`rt-archive-${row.code}`}
                disabled={busy}
              >
                Archive
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
